import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ArtworkCard from '@/components/ArtworkCard';
import NewArrivalsCarousel from '@/components/NewArrivalsCarousel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [featuredArtworks, setFeaturedArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedArtworks();
  }, []);

  const fetchFeaturedArtworks = async () => {
    try {
      // Only fetch main gallery artworks (exclude NSFW)
      const response = await axios.get(`${API}/artworks?gallery_type=main`);
      setFeaturedArtworks(response.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1681684563154-798e01d10fe9)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 text-center text-white px-6">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif text-5xl md:text-7xl tracking-tight leading-none mb-6"
            data-testid="hero-title"
          >
            Curated Art<br />For Your Space
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-lg leading-relaxed mb-8 max-w-2xl mx-auto"
          >
            Original paintings and photo prints by Marco Antoima.
            Each piece is unique and ready to transform your collection.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to="/gallery">
              <Button
                size="lg"
                className="rounded-full px-8 py-6 text-sm uppercase tracking-widest font-medium hover:scale-105 transition-transform duration-300"
                data-testid="explore-gallery-btn"
              >
                EXPLORE GALLERY
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* New Arrivals Carousel */}
      <NewArrivalsCarousel />

      {/* Featured Artworks */}
      <section className="py-24 md:py-32 max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-4" data-testid="featured-title">
            Featured Collection
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
            Hand-picked pieces from our gallery
          </p>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-testid="featured-artworks-grid">
            {featuredArtworks.map((artwork) => (
              <ArtworkCard key={artwork.id} artwork={artwork} />
            ))}
          </div>
        )}

        <div className="text-center mt-16">
          <Link to="/gallery">
            <Button
              variant="outline"
              size="lg"
              className="rounded-full px-8 py-6 text-sm uppercase tracking-widest hover:bg-accent hover:text-accent-foreground transition-colors"
              data-testid="view-all-btn"
            >
              VIEW ALL ARTWORKS
            </Button>
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32 bg-secondary">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-6">
              Art That Speaks
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-6">
              Every piece in this collection is carefully crafted with attention to material, 
              movement, and presence. Working primarily on natural wood and through photography, 
              each artwork tells its own story.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-8">
              From contemporary paintings to striking photography, each artwork is available as a
              unique piece, ensuring your collection remains exclusive.
            </p>
            <Link to="/gallery">
              <Button
                size="lg"
                className="rounded-full px-8 py-6 text-sm uppercase tracking-widest hover:scale-105 transition-transform"
                data-testid="start-collecting-btn"
              >
                START COLLECTING
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}